import {
  getSpeechRecognitionConstructor,
  isSpeechRecognitionSupported,
  SpeechRecognitionErrorCode,
  SpeechRecognitionInstance,
} from './speech-recognition.types';

/** Dictation lifecycle states surfaced to consuming components. */
export type DictationState = 'idle' | 'listening' | 'error';

export interface DictationControllerCallbacks {
  /** Called with interim (not-yet-final) transcript text, if the browser supports interim results. */
  onInterimResult?: (transcript: string) => void;
  /** Called with finalized transcript text. Must never be called twice for the same recognized phrase. */
  onFinalResult: (transcript: string) => void;
  /** Called whenever the internal state transitions. */
  onStateChange: (state: DictationState) => void;
  /** Called on recognition/permission errors, including unsupported browsers. */
  onError?: (code: SpeechRecognitionErrorCode | 'unsupported', message: string) => void;
}

/**
 * Encapsulates the browser Web Speech API (`SpeechRecognition`/`webkitSpeechRecognition`) so consuming components never
 * touch the global browser API directly. One instance manages exactly one recognition session at a time.
 */
export class HxDictationController {
  private recognition: SpeechRecognitionInstance | null = null;
  private state: DictationState = 'idle';

  constructor(private readonly callbacks: DictationControllerCallbacks) {}

  static isSupported(): boolean {
    return isSpeechRecognitionSupported();
  }

  /**
   * Pre-warms the microphone/audio pipeline ahead of a real `start()` call. Browsers pay a real startup cost (device
   * init, permission check) the first time audio capture begins in a session, and that cost otherwise lands on the
   * first `SpeechRecognition.start()` call, causing the first spoken words to be missed. Call this on a strong intent
   * signal (pointer/focus on the mic button) BEFORE the click, so the pipeline is already warm by the time recognition
   * actually starts. Silently no-ops if unsupported, already warmed, or permission is denied/dismissed — the real
   * `start()` call still surfaces any actual error normally.
   */
  static warmUp(): void {
    if (HxDictationController._warmedUp || typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      return;
    }
    HxDictationController._warmedUp = true;

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => stream.getTracks().forEach((track) => track.stop()))
      .catch(() => {
        // Allow retrying on the next hover/focus if warm-up failed (e.g. dismissed prompt).
        HxDictationController._warmedUp = false;
      });
  }

  private static _warmedUp = false;

  get currentState(): DictationState {
    return this.state;
  }

  /** Starts a new recognition session. No-op if already listening. */
  start(lang = 'en-US'): void {
    if (this.state === 'listening') {
      return;
    }

    const RecognitionCtor = getSpeechRecognitionConstructor();
    if (!RecognitionCtor) {
      this.setState('error');
      this.callbacks.onError?.('unsupported', 'Speech recognition is not supported in this browser.');
      return;
    }

    const recognition = new RecognitionCtor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = lang;

    recognition.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0]?.transcript ?? '';
        if (result.isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      if (finalTranscript) {
        this.callbacks.onFinalResult(finalTranscript);
      }
      if (interimTranscript) {
        this.callbacks.onInterimResult?.(interimTranscript);
      }
    };

    recognition.onerror = (event) => {
      this.callbacks.onError?.(event.error, event.message);
      this.releaseRecognition();
      this.setState('error');
    };

    recognition.onend = () => {
      this.releaseRecognition();
      this.setState('idle');
    };

    this.recognition = recognition;

    try {
      recognition.start();
      this.setState('listening');
    } catch {
      this.releaseRecognition();
      this.setState('error');
      this.callbacks.onError?.('aborted', 'Failed to start speech recognition.');
    }
  }

  /** Stops the active recognition session, if any. `onend` will fire and clean up state. */
  stop(): void {
    this.recognition?.stop();
  }

  /** Immediately aborts and releases the recognition session/listeners. Safe to call multiple times. */
  dispose(): void {
    if (this.recognition) {
      try {
        this.recognition.abort();
      } catch {
        // Ignore — recognition may already be stopped.
      }
    }
    this.releaseRecognition();
    this.state = 'idle';
  }

  private releaseRecognition(): void {
    if (this.recognition) {
      this.recognition.onresult = null;
      this.recognition.onerror = null;
      this.recognition.onend = null;
      this.recognition.onstart = null;
    }
    this.recognition = null;
  }

  private setState(state: DictationState): void {
    this.state = state;
    this.callbacks.onStateChange(state);
  }
}
